import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useMediaQuery } from '@material-ui/core';
import cx from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import convert from 'color-convert';
import allActions from '../../../store/actions';
import {
  acceptedStepsSelector,
  currentStepSelector,
  isRedirectFromSummarySelector,
  stepsSelector,
} from '../../../store/selectors/formConfigSelectors';
import { formInitialValuesSelector } from '../../../store/selectors/appSelectors';

const useColorlibStepIconStyles = (isMobileView, isHoverEffectApplied) =>
  makeStyles((theme) => {
    const mainColorRgb = convert.hex.rgb(theme.palette.primary.main);
    return {
      root: {
        '&:hover': {
          cursor: isHoverEffectApplied ? 'pointer' : 'auto',
          opacity: isHoverEffectApplied ? 0.85 : 1,
        },
        zIndex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '50%',
        border: `1px solid ${theme.palette.grey[300]}`,
        fontWeight: 500,
        color: theme.palette.grey[300],
        width: isMobileView ? 32 : 50,
        height: isMobileView ? 32 : 50,
      },
      clickable: {
        background: theme.palette.common.white,
        border: `1px solid ${theme.palette.primary.main}`,
        color: theme.palette.primary.main,
      },
      lightenCompleted: {
        background: `rgba(${mainColorRgb},.4)`,
        border: 'none',
        color: theme.palette.common.white,
      },
      active: {
        background: theme.palette.primary.main,
        border: 'none',
        color: theme.palette.common.white,
      },
      completed: {
        background: theme.palette.primary.main,
        border: 'none',
        color: theme.palette.common.white,
      },
      error: {
        color: theme.palette.error.main,
        border: `1px solid ${theme.palette.error.main}`,
        background: theme.palette.common.white,
      },
    };
  });

const CustomStepIcon = ({ active, completed, icon, formValues, validateForm }) => {
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const dispatch = useDispatch();
  const {
    isRedirectFromSummary,
    currentStep,
    acceptedSteps,
    stepsLength,
    formInitialValues,
  } = useSelector((state) => ({
    // formConfig state
    isRedirectFromSummary: isRedirectFromSummarySelector(state),
    currentStep: currentStepSelector(state),
    acceptedSteps: acceptedStepsSelector(state),
    stepsLength: stepsSelector(state)?.length,
    // app state
    formInitialValues: formInitialValuesSelector(state),
  }));

  const isCircleClickable = () => {
    if (isRedirectFromSummary) return false;
    if (currentStep === stepsLength) return false;

    return acceptedSteps.length >= icon;
  };

  const classes = useColorlibStepIconStyles(isMobileView, isCircleClickable())();

  const isIconError = () => {
    if (currentStep + 1 <= icon) return false;

    return !acceptedSteps[icon - 1];
  };

  const iconClasses = () => {
    if (isRedirectFromSummary) {
      return cx(classes.root, {
        [classes.lightenCompleted]: true,
        [classes.active]: currentStep + 1 === icon,
        [classes.error]: isIconError(),
      });
    }
    return cx(classes.root, {
      [classes.clickable]: isCircleClickable(),
      [classes.active]: active,
      [classes.completed]: completed,
      [classes.error]: isIconError(),
    });
  };

  const handleCircleClick = () => {
    if (!isCircleClickable()) return;

    validateForm().then((errors) => {
      const isDataCorrect = Object.entries(errors).length === 0;
      dispatch(allActions.formConfigActions.setAcceptedSteps(currentStep, isDataCorrect));
      dispatch(allActions.formConfigActions.setRegisterData(formValues, currentStep));
      dispatch(
        allActions.appConfigActions.setFormInitialValues({ ...formInitialValues, ...formValues }),
      );
      dispatch(allActions.formConfigActions.setFormStep(icon - 1));
    });
  };

  return (
    <div onClick={handleCircleClick} className={iconClasses()}>
      {icon}
    </div>
  );
};

CustomStepIcon.propTypes = {
  active: PropTypes.bool.isRequired,
  completed: PropTypes.bool.isRequired,
  icon: PropTypes.number.isRequired,
  validateForm: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  formValues: PropTypes.object,
};
CustomStepIcon.defaultProps = {
  validateForm: null,
  formValues: {},
};

export default CustomStepIcon;
